'use client';

import { Card, type CardType } from '@/components/cms/Card';
import { EntryEffectWrapper } from '@/components/cms/EntryEffectWrapper';
import { MediaWrapper } from '@/components/cms/MediaWrapper';
import type { CMSContentItem } from '@/lib/cms/types';
import type { ImageWithFocalPoint } from '@/lib/contentful/types';
import clsx from 'clsx';
import type { ReactNode } from 'react';
import styles from './ContentGroup.module.scss';

/**
 * ContentGroup layout types
 * Based on Breitling's ContentGroup pattern
 */
export type ContentGroupType =
  | 'DoubleCard' // 2 columns, edge-to-edge
  | 'DoubleCardPadded' // 2 columns, white background + outer padding
  | 'DoubleCardWide' // 2 columns, wider cards
  | 'ThreeColumnStack' // 3 columns, stacked on mobile
  | 'ThreeColumnScroll' // 3 columns, horizontal scroll on mobile
  | 'FourColumnScroll' // 4 columns, horizontal scroll on mobile
  | 'SliderCards' // Horizontal slider
  | 'DynamicLatestNews'; // Two-panel news carousel with world clock wheel

// Generic content item type for ContentGroup items
type ContentItem = CMSContentItem | { __typename: string; sys: { id: string } };

export interface ContentGroupProps {
  /** Layout type determines column structure and behavior */
  type: ContentGroupType;
  /** Items to render in the group - can be ContentfulFragment or ImageWithFocalPoint */
  items: ContentItem[];
  /** Card type for each item (if rendering cards) */
  cardType?: CardType;
  /** Optional heading */
  heading?: string;
  /** Optional subheading */
  subheading?: string;
  /** Optional link text for "see all" */
  linkText?: string;
  /** Optional link URL */
  linkUrl?: string;
  /** Background color */
  backgroundColor?: string;
  /** Render items as MediaWrapper instead of Card */
  mediaOnly?: boolean;
  /** Optional className */
  className?: string;
  /** Children for custom rendering */
  children?: ReactNode;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** HTML anchor ID for navigation */
  id?: string | null;
}

/**
 * ContentGroup component
 *
 * A meta-component that renders different layouts based on `type` prop.
 * Follows Breitling's ContentGroup pattern.
 *
 * @example
 * // Double column layout with cards
 * <ContentGroup
 *   type="DoubleCard"
 *   items={[leftCardData, rightCardData]}
 *   cardType="MediaBanner"
 * />
 *
 * @example
 * // Three column with media only
 * <ContentGroup
 *   type="ThreeColumnStack"
 *   items={[img1, img2, img3]}
 *   mediaOnly
 * />
 */
export function ContentGroup({
  type,
  items,
  cardType = 'default',
  heading,
  subheading,
  linkText,
  linkUrl,
  backgroundColor,
  mediaOnly = false,
  className,
  children,
  cmsIndex,
  id,
}: Readonly<ContentGroupProps>) {
  // Helper to generate test IDs: e.g., "contentgroup-0-section"
  const testIdPrefix = cmsIndex !== undefined ? `contentgroup-${cmsIndex}` : undefined;
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);
  // Get column count based on type
  const getColumnCount = () => {
    switch (type) {
      case 'DoubleCard':
      case 'DoubleCardPadded':
      case 'DoubleCardWide':
        return 2;
      case 'ThreeColumnStack':
      case 'ThreeColumnScroll':
        return 3;
      case 'FourColumnScroll':
        return 4;
      case 'SliderCards':
        return items.length; // Slider shows all
      default:
        return 3;
    }
  };

  const isSlider =
    type === 'SliderCards' || type === 'ThreeColumnScroll' || type === 'FourColumnScroll';
  const isFillLayout = type === 'DoubleCard' || type === 'DoubleCardPadded';
  const columns = getColumnCount();

  // Render individual items
  const renderItem = (item: ContentItem, index: number) => {
    const itemId = 'sys' in item ? item.sys.id : String(item);
    const itemTestId = testIdPrefix ? `${testIdPrefix}-item-${index}` : undefined;
    const childHasEffect = (item as { entryEffect?: boolean }).entryEffect ?? false;

    let content: ReactNode;
    if (mediaOnly && item.__typename === 'ImageWithFocalPoint') {
      content = <MediaWrapper media={item as ImageWithFocalPoint} mediaType="Grid" />;
    } else {
      content = <Card type={cardType} data={item as CMSContentItem} fillContainer={isFillLayout} />;
    }

    return (
      <div key={itemId} className={styles.item} data-testid={itemTestId}>
        {childHasEffect ? (
          <EntryEffectWrapper enabled variant="standard">
            {content}
          </EntryEffectWrapper>
        ) : (
          content
        )}
      </div>
    );
  };

  return (
    <section
      id={id || undefined}
      className={clsx(styles.section, styles[type], className)}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid={testId('section')}
    >
      {/* Optional Header */}
      {(heading || linkText) && (
        <div className={styles.header}>
          <div className={styles.headerText}>
            {heading && (
              <h2 className={styles.heading} data-testid={testId('title')}>
                {heading}
              </h2>
            )}
            {subheading && (
              <p className={styles.subheading} data-testid={testId('subtitle')}>
                {subheading}
              </p>
            )}
          </div>
          {linkText && linkUrl && (
            <a href={linkUrl} className={styles.link} data-testid={testId('link')}>
              {linkText}
            </a>
          )}
        </div>
      )}

      {/* Content Grid/Slider */}
      <div
        className={clsx(styles.container, styles[`columns-${columns}`], isSlider && styles.slider)}
      >
        {children || items.map((item, index) => renderItem(item, index))}
      </div>
    </section>
  );
}

ContentGroup.displayName = 'ContentGroup';
